import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Black, Gray900, Primary500, Attention500 } from '@mehilainen/mds-customer/colors'
import { CircleInfo } from '@mehilainen/mds-customer/icons'
import { spacing } from '@mehilainen/mds-tokens/spacing'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useIsMobile } from '../../hooks/useBreakpoint'
import Avatar, { resourceListImage } from '../Avatar/Avatar'
import { CenteredRowFlex, ColumnFlex } from '../Layout/Layout'
import { Text } from '../Typography/Typography'

import { ClickableText } from './styles'

type VariantOptions = 'XS' | 'S' | 'M' | 'L'
export interface Props {
  name: string
  title: string
  isResourceList?: boolean
  image?: string | null
  rating?: number
  allowsNewKelaClients?: boolean
  variant?: VariantOptions
  className?: string
  onClick?(): void
  extraElement?: React.ReactNode
}

const PointerCursor = css`
  cursor: pointer;
`

const VerticalContainer = styled(ColumnFlex)`
  gap: ${spacing[500].rem};
`

const Container = styled(CenteredRowFlex)<{ isClickable: boolean }>`
  gap: ${spacing[500].rem};
  ${(props) => props.isClickable && PointerCursor}
`

const TextContainer = styled(ColumnFlex)`
  gap: ${spacing[100].rem};
  align-items: start;
  text-align: start;
`

const TextWithIconContainer = styled(Text)<{ $align: 'center' | 'flex-start' }>`
  display: flex;
  gap: ${spacing[200].rem};
`

const Title = styled(Text)`
  word-break: break-word;
`

const AppointmentSpecialistDetails: React.FC<React.PropsWithChildren<Props>> = ({
  name,
  title,
  isResourceList,
  image,
  rating,
  allowsNewKelaClients,
  variant = 'L',
  className,
  onClick,
  extraElement,
}) => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()

  const onClickHandler = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      onClick?.()
    },
    [onClick]
  )

  const onKeyHandler = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        event.stopPropagation()
        onClick?.()
      }
    },
    [onClick]
  )

  const isClickable = !!onClick && !isResourceList

  return (
    <VerticalContainer>
      <Container isClickable={isClickable} className={className}>
        <Avatar
          width={variant === 'XS' ? '2.5rem' : '4rem'}
          height={variant === 'XS' ? '3.125rem' : '5.25rem'}
          ratingFontSize="11px"
          ratingPadding="4px"
          borderRadius="10px"
          image={isResourceList ? resourceListImage : image}
          rating={isResourceList ? undefined : rating}
          onClick={isClickable ? onClickHandler : undefined}
          onKeyDown={isClickable ? onKeyHandler : undefined}
          tabIndex={-1}
        />
        <TextContainer>
          <ClickableText
            $size={400}
            $height="Medium"
            $weight="Medium"
            $color={isClickable ? `${Primary500}` : `${Black}`}
            role={isClickable ? 'button' : undefined}
            onClick={isClickable ? onClickHandler : undefined}
            onKeyDown={isClickable ? onKeyHandler : undefined}
            tabIndex={isClickable ? 0 : -1}
            data-analytics-path="doctor_modal_click_id_13070"
            clickable={isClickable}
          >
            <span data-cy="specialistName">{name}</span>
          </ClickableText>
          {(variant === 'M' || variant === 'L') && (
            <Title $size={300} $height="Medium" $weight="Regular" $color={Gray900}>
              {title}
            </Title>
          )}
          {!isMobile && allowsNewKelaClients === false && (
            <TextWithIconContainer $size={200} $color={Attention500} $align="flex-start">
              <CircleInfo />
              <span>{t('component.appointmentList.disallowsKelaClients')}</span>
            </TextWithIconContainer>
          )}
          {extraElement}
        </TextContainer>
      </Container>
      {isMobile && allowsNewKelaClients === false && (
        <TextWithIconContainer $size={200} $color={Attention500} $align="flex-start">
          <CircleInfo />
          <span>{t('component.appointmentList.disallowsKelaClients')}</span>
        </TextWithIconContainer>
      )}
    </VerticalContainer>
  )
}

export default AppointmentSpecialistDetails
